import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Form, Formik } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Dialog } from 'src/components/Dialog';
import { Divider } from 'src/components/Divider';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { Visit } from 'src/models/visit/_types';
import { updateVisit } from 'src/models/visit/visit';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import { UserNotes } from 'src/shared/UserNotes';
import api from 'src/utils/ajax';
import toast from 'src/utils/toast';

interface VisitNotesDialogProps {
  open: boolean;
  onClose: () => void;
  visit: Visit | null;
  onVisitUpdate?: (visit: Visit) => void;
}

/**
 * Dialog for managing private notes for a visit.
 * Shows visit's private notes field and user's note history using UserNotes component.
 */
export const VisitNotesDialog: FC<VisitNotesDialogProps> = ({
  open,
  onClose,
  visit,
  onVisitUpdate,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'calendar.viewEventDialog.visitNotesDialog' });
  const queryClient = useQueryClient();

  // Update visit private notes
  const updateVisitMutation = useMutation({
    mutationFn: (privateNotes: string) =>
      updateVisit({
        id: visit!.id,
        privateNotes,
      }),
    onSuccess: (updatedVisit) => {
      toast.success(t('updated'));
      onVisitUpdate?.(updatedVisit);
      queryClient.invalidateQueries({ queryKey: ['visits'] });
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
    },
    onError: (error) => {
      toast.error(api.formatError(error, t('updateFailed')));
    },
  });

  if (!visit) return null;

  const userName = visit.userName || 'User';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('title', { userName })}
      maxWidth="md"
      fullWidth
    >
      <Stack spacing={3}>
        {/* Visit-specific private notes */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {t('visitNotes')}
          </Typography>
          <Formik
            initialValues={{ privateNotes: visit.privateNotes || '' }}
            onSubmit={async (values) => {
              await updateVisitMutation.mutateAsync(values.privateNotes);
            }}
            enableReinitialize
          >
            {({ submitForm, dirty }) => (
              <Form>
                <Stack spacing={1}>
                  <FormTextField
                    name="privateNotes"
                    placeholder={t('placeholder')}
                    multiline
                    rows={3}
                  />
                  {dirty && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={submitForm}
                        disabled={updateVisitMutation.isPending}
                      >
                        {t('saveVisitNotes')}
                      </Button>
                    </Box>
                  )}
                </Stack>
              </Form>
            )}
          </Formik>
        </Box>

        <Divider />

        {/* User's notes history using UserNotes component */}
        {visit.userId && (
          <UserNotes userId={visit.userId} embedded defaultTypeFilter="Visit" />
        )}
      </Stack>
    </Dialog>
  );
};
